// Hook telemetry — real-world numbers for what the evals measure in the lab
// (ms per injection, tokens, cache hit rate). The design constraint is the
// hook's own contract: ZERO added latency on the edit path. pre-edit only
// appends one line to a local buffer (sync fs write, microseconds); the
// detached `hook sync` child — already running in the background — drains the
// buffer to PostHog where waiting costs nothing.
import { existsSync, readFileSync, writeFileSync, mkdirSync, rmSync } from 'node:fs'
import { resolve, dirname } from 'node:path'
import { driftlessHome } from './api-client'

export interface HookMetric {
  at: number
  ms: number
  tokens: number
  cache_hit: boolean
  matched: number
  injected: boolean
}

const MAX_BUFFERED = 200 // a runaway session can't grow the file unbounded

function bufferPath(ws: string): string {
  return resolve(driftlessHome(), `hook-metrics-${ws.replace(/[^a-zA-Z0-9_-]/g, '')}.jsonl`)
}

export function recordHookMetric(ws: string, m: HookMetric): void {
  try {
    const p = bufferPath(ws)
    mkdirSync(dirname(p), { recursive: true })
    const lines = existsSync(p) ? readFileSync(p, 'utf8').split('\n').filter(Boolean) : []
    lines.push(JSON.stringify(m))
    writeFileSync(p, lines.slice(-MAX_BUFFERED).join('\n') + '\n')
  } catch {
    /* telemetry must never break an edit */
  }
}

// Read-and-clear. The caller (the detached sync child) owns delivery; a
// failed send simply loses that batch — metrics are a signal, not a ledger.
export function drainHookMetrics(ws: string): HookMetric[] {
  try {
    const p = bufferPath(ws)
    if (!existsSync(p)) return []
    const out = readFileSync(p, 'utf8')
      .split('\n')
      .filter(Boolean)
      .map((l) => {
        try { return JSON.parse(l) as HookMetric } catch { return null }
      })
      .filter((m): m is HookMetric => m !== null)
    rmSync(p)
    return out
  } catch {
    return []
  }
}

// One aggregate event per drain, not one per edit — hit rates and percentiles
// are what the funnel needs; per-edit rows would just be PostHog noise.
export function aggregateMetrics(ms: HookMetric[]): Record<string, number> | null {
  if (ms.length === 0) return null
  const sorted = [...ms].sort((a, b) => a.ms - b.ms)
  const p = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))].ms
  return {
    edits: ms.length,
    injected: ms.filter((m) => m.injected).length,
    cache_hits: ms.filter((m) => m.cache_hit).length,
    ms_p50: p(0.5),
    ms_p95: p(0.95),
    tokens_avg: Math.round(ms.reduce((s, m) => s + m.tokens, 0) / ms.length),
  }
}
